import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from '../../actions/orderActions'
import ListFood from '../../components/list-food/ListFood'
import MenuGroupFood from '../../components/menu-group-food'
import { FOOD_KIND } from '../../constants'
import useDidMountEffect from '../../hooks/useDidMountEffect'
import useDisplayGroup from '../../hooks/useDisplayGroup'
import useOrder from '../../hooks/useOrder'
import {
    countCart,
    getCurrentGroupOrder,
    getFoodGroup,
    getRestaurantSettings,
} from '../../selectors'
import { getFoodById, getListFoodAdded } from '../../selectors/order'
import { getFoodPriceShow } from '../../utils'
import {
    disableBrowserBack,
    ensureArray,
    getBodyScrollTop,
} from '../../utils/helper'
import { getActionViewFood } from '../../utils/view'
import CardShortcutContainer from './CardShortcutContainer'
import FoodInCartModal from './FoodInCartModal'
import ModalUpdateNote from './ModalUpdateNote'

const ListFoodContainer = () => {
    const foodsGrouped = useSelector(state => state.FoodReducer)
    const foodGroupInfo = useSelector(getFoodGroup)
    const cartAmount = useSelector(countCart)
    const foodOrdering = useSelector(getListFoodAdded)
    const { in_vat = 0 } = useSelector(getRestaurantSettings)
    const curentGroupOrder = useSelector(getCurrentGroupOrder)

    const { addToCart } = useOrder();

    const [foodEditNote, setFoodEditNote] = useState(false)
    const foodWillUpdateNote = useSelector(getFoodById(foodEditNote))
    const [viewFoodInCart, setViewFoodInCart] = useState(null)
    const [scrollTopPosition, setScrollTopPosition] = useState(0)
    const listGroupRef = useRef([])

    const dispatch = useDispatch()
    const {
        handleSetDisplayGroup,
        displayGroup,
        setDisplayGroup,
        refChangeByClick,
    } = useDisplayGroup({ isMobile: true })

    const handleViewFoodInCart = food => {
        setViewFoodInCart(food)
    }

    const handleViewFoodDetails = food => {
        dispatch(getActionViewFood(food))
    }

    const handleAddNew = () => {
        handleViewFoodDetails(viewFoodInCart)
        setViewFoodInCart(null)
    }

    const addGroup = (arrID, food) => {
        dispatch(
            actions.addFoodGroupToCart({
                params: {
                    arrID,
                    vat: in_vat,
                    amount: 1,
                    note: '',
                    beilageItems: [],
                    index: -1,
                },
                onDone: addImmediately => {
                    !addImmediately && handleViewFoodDetails(food)
                },
            })
        )
    }

    const handleFastAddToCart = food => {
        if (food.pluGroup && food.kind !== FOOD_KIND.FOOD_CHOICE) {
            addGroup(
                food.pluGroup.split(';').filter(el => !!el),
                food
            )

            return
        }

        const hasBeilage = food.beilageItems.length
        if (hasBeilage || food.kind === FOOD_KIND.FOOD_CHOICE) {
            return handleViewFoodDetails(food)
        }

        const { currentPrice, saleOfPercent, isHappyHour } = getFoodPriceShow(food);

        addToCart({
            amount: 1,
            note: '',
            beilageItems: [],
            index: -1,
            foodDetails: {
                ...food,
                currentPrice: food.currentPrice ?? currentPrice,
                saleOfPercent,
            },
            isHappyHour,
            vat: in_vat,
            groupOrder: curentGroupOrder
        });
    }

    const handleUpdateAmount = (amount, index, id, groupOrder) => {
        dispatch(
            actions.updateAmount({
                amount,
                index,
                id,
                groupOrder,
            })
        )
    }

    const handleSubmitNote = note => {
        if (!foodWillUpdateNote) {
            return false
        }

        dispatch(
            actions.updateNote({
                note,
                id: foodWillUpdateNote?.foodDetails?.id,
                groupOrder: curentGroupOrder,
                index: foodWillUpdateNote?.indexInCart,
            })
        )

        setFoodEditNote(false)
    }

    const handleCloseUpdateNote = () => setFoodEditNote(false)

    const checkInView = el =>
        el.getBoundingClientRect().y < window.innerHeight / 2

    const handleScroll = () =>
        setScrollTopPosition(getBodyScrollTop().scrollTop)

    const handleClickMenu = gID => {
        refChangeByClick.current = true
        handleSetDisplayGroup(gID, () => {})
    }

    const getIds = () => {
        if (!viewFoodInCart) {
            return []
        }

        if (viewFoodInCart?.subs) {
            return viewFoodInCart?.subs.map(el => el.plu)
        }

        return [viewFoodInCart?.plu]
    }

    useDidMountEffect(() => {
        const timeOut = setTimeout(() => {
            if (refChangeByClick.current) {
                return (refChangeByClick.current = false)
            }

            const listChilds = ensureArray(listGroupRef.current)
            let childActive = ''
            for (let i = listChilds.length - 1; i > -1; i--) {
                const child = listChilds[i]
                if (child && checkInView(child)) {
                    childActive = +child.getAttribute('group-data')
                    break
                }
            }

            childActive &&
                childActive !== displayGroup &&
                setDisplayGroup(childActive)
        }, 100)
        return () => {
            timeOut && clearTimeout(timeOut)
        }
    }, [scrollTopPosition])

    useEffect(() => {
        disableBrowserBack()
        getBodyScrollTop().addEventListener('scroll', handleScroll)

        return () => {
            getBodyScrollTop().removeEventListener('scroll', handleScroll)
        }
    }, [])

    return (
        <>
            <FoodInCartModal
                foodIds={getIds()}
                handleClose={() => setViewFoodInCart(null)}
                handleAddNew={handleAddNew}
                handleUpdateAmount={handleUpdateAmount}
            />

            <ModalUpdateNote
                food={foodWillUpdateNote}
                handleCloseUpdateNote={handleCloseUpdateNote}
                handleSubmitNote={handleSubmitNote}
            />

            <div className="list-food-wrap">
                {foodGroupInfo.map((group, index) => {
                    const foods = foodsGrouped[group.id];

                    if (!foods) {
                        return null;
                    }

                    return (
                        <ListFood
                            handleUpdateAmount={handleUpdateAmount}
                            handleViewFoodInCart={handleViewFoodInCart}
                            handleFastAddToCart={handleFastAddToCart}
                            setFoodSelected={handleViewFoodDetails}
                            setFoodEditNote={setFoodEditNote}
                            key={group.id}
                            id={`group-${group.id}`}
                            group={group}
                            groupRef={ref => (listGroupRef.current[index] = ref)}
                            list={foods.map(f => {
                                let exitsInCart = false,
                                    currentAmount = 0

                                let listFoodInCart = foodOrdering.filter(
                                    fOrdering =>
                                        (fOrdering.plu ??
                                            fOrdering.foodDetails?.plu) === f.plu
                                )

                                if (f.subs) {
                                    const ids = f.subs.map(foodSub => foodSub.plu)
                                    listFoodInCart = foodOrdering.filter(
                                        fOrdering =>
                                            ids.includes(
                                                fOrdering.plu ??
                                                    fOrdering.foodDetails?.plu
                                            )
                                    )
                                }

                                if (listFoodInCart.length) {
                                    exitsInCart = true
                                    currentAmount = listFoodInCart.reduce(
                                        (sum, cr) => sum + cr.amount,
                                        0
                                    )
                                }

                                if (exitsInCart) {
                                    return {
                                        ...f,
                                        exitsInCart,
                                        currentAmount,
                                    }
                                }

                                return f
                            })}
                        />
                    );
                })}
            </div>

            <CardShortcutContainer
                menuUI={
                    <MenuGroupFood
                        groups={foodGroupInfo
                            .map(el => ({
                                ...el,
                                count: ensureArray(foodsGrouped[el.id]).length,
                            }))
                            .filter(g => !!g.count)}
                        handleSetDisplayGroup={handleClickMenu}
                        displayGroup={displayGroup}
                        cartAmount={cartAmount}
                    />
                }
            />
        </>
    )
}

export default ListFoodContainer
